import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react'
import { Link } from 'react-router-dom'
import {
    Alert,
    Card,
    CardContent,
    CardHeader,
    Grid,
    List,
    ListItem,
    ListItemButton,
    Typography,
    Box,
    TextField,
    IconButton,
    Stack,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faAngleRight } from '@fortawesome/pro-duotone-svg-icons/faAngleRight'
import { faPlus } from '@fortawesome/pro-duotone-svg-icons/faPlus'
import { faPortrait } from '@fortawesome/pro-duotone-svg-icons/faPortrait'
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes'
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown'

import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import SearchDefDossier from '@/feature/DefDossier/Utils/SearchDefDossier'
import { Trans } from 'react-i18next'
import SelectRC, { Option } from 'rc-select' // Keep for now, might need to replace later
import DisplayItemBlock from './DisplayItemBlock'
import BrpModal from '../Modules/Brp/BrpModal'
import DefDossierList from '@/feature/DefDossier/Index/Components/DefDossierList'
import NewContentDossier from '@/feature/ContentDossier/Create/CreateContentDossier'
import ContentDossier from '@/feature/ContentDossier/Detail/ContentDossier'
import Loading from '@/Components/Elements/Loading/LoadingData'
import AppDialog from '@/Components/AppDialog/AppDialog'
import {
    CompactButton,
    FilterToggleButton,
    StyledButton,
} from '@/Components/Elements/StyledComponents/Buttons'
import { brandedTokens } from '@/theme/tokens'

interface LinkFieldProps {
    kedo: any
    item: {
        id: number
        def_dossier_link: {
            id: number
            child_def_dossier_id: number
            back_link_id: number
            settings: {
                multiple?: boolean
                hide_add?: boolean
                limit_embedded_link?: number
                context_filter?: any[]
                limit_output_to_mis_item?: string
                sort?: any[]
            }
        }
        settings?: {
            only_allow_add?: boolean
            enable_small_view?: boolean
            show_id?: boolean
            show_brp?: boolean
            clickable?: boolean
            show_embedded?: boolean
            block?: number
            embedded_single_view?: boolean
            passing_sidebar?: any
            default_val_dos_query?: string
        }
        def_dossier_id?: number
    }
    value?: any[]
    changeValue?: (id: number, value: any) => void
    errors?: string
    dossier?: {
        id: number
    }
    embedded?: number
    linkId?: number
    mode?: string
}

interface SelectedItem {
    id: number
    summary?: string
    loading?: boolean
    archived?: boolean
    value?: number
    label?: string
}

interface LinkFieldRef {
    doFocus: () => void
    getValue: () => number[]
    onCancel: () => void
}

const LinkField = forwardRef<LinkFieldRef, LinkFieldProps>((props, ref) => {
    const innerRef = useRef<HTMLDivElement>(null)

    const initialSelected: SelectedItem[] = props.value
        ? props.value.map((item) => ({ ...item }))
        : []

    if (
        !props.dossier &&
        props.embedded &&
        props.item.def_dossier_link.id === props.linkId
    ) {
        initialSelected.push({ id: props.embedded })
    }

    const [state, setState] = useState({
        opened: false,
        initial: [] as any[],
        linkSearch: [] as any[],
        searchDossierQuery: '',
        dpChildMain: null as any,
        options: [] as any[],
        optionContents: [] as any[],
        filteredOptions: null as any[],
        optionsLoading: false,
        page: 0,
        pages: 0,
        search: '',
        searchItems: [] as any[],
        searchItemsInitialized: false,
        selected: initialSelected,
        total: 0,
        value: props.value || [],
        optAsyn: [] as any[],
        showBrp: false,
        showFilter: false,
        addDossier: false,
        loading: false,
        multiAdd: false,
        searchItemsLoading: false,
    })

    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    useEffect(() => {
        let selected: SelectedItem[] = []
        if (props.value && props.value.length > 0) {
            selected = props.value.map((item) => ({ ...item }))
        }

        // If the dossier is embedded in another dossier and its a new dossier we will select this link automatic.
        if (
            !props.dossier &&
            props.embedded &&
            props.item.def_dossier_link.id === props.linkId
        ) {
            selected.push({ id: props.embedded })
        }

        setState((prev) => ({ ...prev, selected, value: props.value || [] }))
    }, [
        props.value,
        props.dossier,
        props.embedded,
        props.item.def_dossier_link.id,
        props.linkId,
    ])

    const doFocus = () => {
        if (innerRef.current) {
            setTimeout(() => innerRef.current?.focus(), 1)
            window.scrollTo(0, innerRef.current.offsetTop)
        }
    }

    const getValue = (): number[] => {
        return state.selected.map((i) => i.id)
    }

    const onCancel = () => {
        let selected: SelectedItem[] = []
        if (props.value && props.value.length > 0) {
            selected = props.value.map((item) => ({ ...item }))
        }
        if (
            !props.dossier &&
            props.embedded &&
            props.item.def_dossier_link.id === props.linkId
        ) {
            selected.push({ id: props.embedded })
        }
        setState((prev) => ({
            ...prev,
            selected,
            value: props.value || [],
        }))
    }

    useImperativeHandle(ref, () => ({
        doFocus,
        getValue,
        onCancel,
    }))

    const noHideAdd = () => {
        const settings = props.item.settings
        if (!settings || !settings.hide_add || settings.hide_add !== true) {
            return true
        }
        return settings.hide_add === true ? false : true
    }

    const isSingleSelect = () => {
        const settings = props.item.def_dossier_link.settings
        if (settings && settings.multiple && settings.multiple === true) {
            return false
        }
        return true
    }

    const removeSelectedItem = (item: SelectedItem) => {
        const removed = state.selected.filter((opt) => opt.id !== item.id)
        setState((prev) => ({ ...prev, selected: removed }))
        if (props.changeValue) {
            props.changeValue(props.item.id, removed)
        }
    }

    const addSelectedItem = (item: SelectedItem) => {
        let select = state.selected
        if (isSingleSelect() && select.length > 0) {
            return
        }
        select = select.filter((opt) => opt.id !== item.id)
        select.push(item)
        const ids = select.map((item) => item.id)
        if (props.changeValue) {
            props.changeValue(props.item.id, select)
        }
        setState((prev) => ({ ...prev, selected: select }))
    }

    const getChildDefDossierId = () => {
        return props.item.def_dossier_link.child_def_dossier_id
    }

    const getOptions = (
        nextPage?: boolean,
        setValuesDefaultQuery?: string,
        linkSearch = state.linkSearch,
        searchDossierQuery = state.searchDossierQuery,
        search = state.search
    ) => {
        if (props.item.settings?.only_allow_add === true) {
            return
        }

        const kedo = props.kedo
        const params = {
            params: {
                defDossier: getChildDefDossierId(),
                environment: kedo.env().getEnvironment().id,
                limit: 25,
            },
        }
        if (props.embedded && props.item.settings?.limit_embedded_link) {
            params.params.linkId = props.item.settings.limit_embedded_link
            params.params.embedded = props.embedded
        }

        if (nextPage === true && state.page < state.pages) {
            params.params.page = state.page + 1
        }

        if (search) {
            params.params.summary = search
        }

        if (
            props.item.settings?.context_filter &&
            props.item.settings.context_filter.length > 0
        ) {
            params.params.context_filter = props.item.settings.context_filter
            params.params.context_id = props.dossier?.id
        }

        if (linkSearch) {
            linkSearch.forEach((searchItem) => {
                if (typeof searchItem.value === 'string') {
                    params.params[searchItem.id] = searchItem.value
                } else if (searchItem.value instanceof Array) {
                    const values: number[] = []
                    searchItem.value.forEach((valItem: any) =>
                        values.push(valItem.id)
                    )
                    params.params[searchItem.id] = values
                } else if (searchItem.value?.custom == 'date') {
                    params.params[`${searchItem.id}_type`] =
                        searchItem.value.type
                    params.params[`${searchItem.id}`] = searchItem.value.date1
                    if (searchItem.value.type == 'between') {
                        params.params[`${searchItem.id}_end`] =
                            searchItem.value.date2
                    }
                }
            })
        }

        // Limit the options available to a dossierQuery filter
        if (props.item.def_dossier_link.settings?.limit_output_to_mis_item) {
            params.params.dossierQuery =
                props.item.def_dossier_link.settings.limit_output_to_mis_item
        } else if (searchDossierQuery) {
            params.params.dossierQuery = searchDossierQuery
        }

        if (setValuesDefaultQuery) {
            params.params.dossierQuery = setValuesDefaultQuery
        }

        if (props.item?.settings?.sort?.length > 0) {
            params.params.sort = []
            params.params.sortOrder = []
            props.item.settings.sort.forEach((filterItem) => {
                params.params.sort.push(filterItem.dddfId)
                params.params.sortOrder.push(filterItem.sort)
            })
        }

        setState((prev) => ({ ...prev, optionsLoading: true }))

        kedo.api()
            .get(kedo.api().getContentDossierEndpoint(), params)
            .then((response: any) => {
                if (setValuesDefaultQuery) {
                    if (response.data?.results?.dossiers) {
                        response.data.results.dossiers.forEach(
                            (dossier: any) => {
                                addSelectedItem(dossier)
                            }
                        )
                    }
                    setState((prev) => ({ ...prev, optionsLoading: false }))
                } else {
                    setState((prev) => ({
                        ...prev,
                        options: nextPage
                            ? [
                                  ...prev.options,
                                  ...response.data.results.dossiers,
                              ]
                            : response.data.results.dossiers,
                        optionContents: nextPage
                            ? [
                                  ...prev.optionContents,
                                  ...response.data.results.contents,
                              ]
                            : response.data.results.contents,
                        page: response.data.pager.current,
                        pages: response.data.pager.pages,
                        total: response.data.pager.items,
                        optionsLoading: false,
                    }))
                }
            })
    }

    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const searchOption = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value

        setState((prev) => ({ ...prev, search: value }))

        if (timeoutRef.current) clearTimeout(timeoutRef.current)

        timeoutRef.current = setTimeout(() => {
            getOptions(false, undefined, undefined, undefined, value)
        }, 500)
    }

    const getBackLinkId = () => {
        return props.item.def_dossier_link.back_link_id
    }

    const getSelectableOptions = (options: any[], selects: SelectedItem[]) => {
        return options.filter((opt) => {
            const found = selects.find((select) => opt.id === select.id)
            return !found || found.length <= 0
        })
    }

    const isInvalid = () => {
        return props.errors && props.errors.length > 0
    }

    const onCreated = (newDossierId: number) => {
        if (newDossierId > 0) {
            const selected = state.selected
            selected.push({
                id: newDossierId,
                summary: props.kedo.t('Loading') + '...',
                loading: true,
            })
            setState((prev) => ({ ...prev, addDossier: false, selected }))
            props.kedo
                .api()
                .get(
                    props.kedo.api().getContentDossierEndpoint() +
                        '/' +
                        newDossierId
                )
                .then((response: any) => {
                    if (response.data && response.data.dossier) {
                        const selected = state.selected.filter(
                            (selected) => selected.id !== newDossierId
                        )
                        selected.push({
                            id: newDossierId,
                            summary: response.data.dossier.summary,
                        })
                        setState((prev) => ({ ...prev, selected }))
                    }
                })
        } else {
            setState((prev) => ({ ...prev, addDossier: false }))
        }
    }

    const renderAddDossier = () => {
        let defDossier = null
        if (
            props.item.def_dossier_link &&
            props.item.def_dossier_link.child_def_dossier_id
        ) {
            defDossier = props.kedo
                .env()
                .getDefDossierById(
                    props.item.def_dossier_link.child_def_dossier_id
                )
        }

        return (
            <AppDialog
                aria-labelledby="contained-modal-title-vcenter"
                open
                maxWidth={'xl'}
                fullWidth
                title={
                    <>
                        {props.kedo.t('New')}{' '}
                        {defDossier
                            ? props.kedo
                                  .env()
                                  .translateItem(defDossier, 'defdossier')
                            : props.kedo.t('Dossier')}
                    </>
                }
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setState((prev) => ({ ...prev, addDossier: false }))
                    }
                }}
                headerClose={() =>
                    setState((prev) => ({ ...prev, addDossier: false }))
                }
                contentProps={{ dividers: true }}
            >
                <NewContentDossier
                    kedo={props.kedo}
                    props={{
                        defDossierId: getChildDefDossierId(),
                        linkId: getBackLinkId(),
                        embedded: true,
                        location: 'EmbeddedLinkField',
                        onClose: () =>
                            setState((prev) => ({
                                ...prev,
                                addDossier: false,
                            })),
                        onSuccess: onCreated,
                    }}
                />
            </AppDialog>
        )
    }

    const fetchSearchFilterItems = () => {
        // Do not get the search items twice!
        if (state.searchItemsInitialized || state.searchItems.length > 0) {
            return
        }

        if (
            props.item.settings &&
            props.item.settings.only_allow_add === true
        ) {
            return
        }

        const params = {
            params: {
                view: ['search', 'advanced-search'],
                defDossier: getChildDefDossierId(),
            },
        }
        setState((prev) => ({ ...prev, searchItemsLoading: true }))
        props.kedo
            .api()
            .getCached(props.kedo.api().getDisplayItemEndpoint(), params)
            .then((response: any) => {
                setState((prev) => ({
                    ...prev,
                    searchItemsInitialized: true,
                    searchItemsLoading: false,
                    searchItems: response.data.results,
                }))
            })
    }

    const getDefDossierFromId = (defDossierId: number) => {
        return props.kedo
            .env()
            .getCurrentEnvironmentDefDossiers()
            .find((item: any) => item.id === parseInt(defDossierId.toString()))
    }

    const search = (values: any) => {
        setState((prev) => ({ ...prev, search: values }))
    }

    const doLinkSearch = (
        searchParams: any[],
        dossierQuery: string,
        archived: boolean
    ) => {
        setState((prev) => ({
            ...prev,
            linkSearch: searchParams,
            searchDossierQuery: dossierQuery,
            showFilter: false,
        }))
        getOptions(false, undefined, searchParams, dossierQuery)
    }

    const renderFilter = () => {
        const searchItems = state.searchItems.filter(
            (searchItem) =>
                searchItem.view === 'search' ||
                searchItem.view === 'advanced-search'
        )
        return (
            <AppDialog
                aria-labelledby="contained-modal-title-vcenter"
                open
                maxWidth={'xl'}
                fullWidth
                title={props.kedo.t('Search')}
                onClose={() =>
                    setState((prev) => ({ ...prev, showFilter: false }))
                }
                headerClose={() =>
                    setState((prev) => ({ ...prev, showFilter: false }))
                }
                contentProps={{ dividers: true }}
            >
                <div>
                    {state.searchItemsLoading ? <LoadingDefault /> : null}
                    {state.searchItemsInitialized ? (
                        <SearchDefDossier
                            doSearch={doLinkSearch}
                            resetSearch={() =>
                                setState((prev) => ({
                                    ...prev,
                                    search: null,
                                    searchDossierQuery: null,
                                }))
                            }
                            kedo={props.kedo}
                            defDossier={getDefDossierFromId(
                                props.item.def_dossier_link.child_def_dossier_id
                            )}
                            items={searchItems}
                            search={state.search}
                            location={'EmbeddedLinkField'}
                        />
                    ) : null}
                </div>
            </AppDialog>
        )
    }

    const renderEdit = () => {
        // If the dossier is embedded in another dossier and its a new dossier we will hide the field, the values is already set.
        if (
            props.embedded &&
            !props.dossier &&
            props.item.def_dossier_link.id === props.linkId
        ) {
            return ''
        }

        const kedo = props.kedo
        const totals = state.total
        let checkSelectedId = null
        let matchCounter = 0

        if (props.item?.settings?.enable_small_view === true) {
            const vals = state.selected.map((item) => ({
                id: item.value ? item.value : item.id,
                label: item.summary ? item.summary : item.label,
                value: item.value ? item.value : item.id,
            }))

            const filteredOptions = state.options.filter((option) => {
                return isSingleSelect()
                    ? option.id !== state.selected?.[0]?.id
                    : !state.selected.some(
                          (selected) => selected.id === option.id
                      )
            })

            return (
                <Box>
                    {state.addDossier ? renderAddDossier() : null}

                    {noHideAdd() &&
                    kedo
                        .env()
                        .isAllowedDefDossier(
                            'create',
                            getChildDefDossierId(),
                            kedo.user()
                        ) &&
                    (isSingleSelect() !== true ||
                        state.selected.length <= 0) ? (
                        <CompactButton
                            onClick={() =>
                                setState((prev) => ({
                                    ...prev,
                                    addDossier: true,
                                }))
                            }
                            sx={{ float: 'right', ml: 2 }}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </CompactButton>
                    ) : null}
                    <Grid container>
                        <Grid ref={innerRef} size={12}>
                            <SelectRC
                                labelInValue
                                showSearch
                                allowClear
                                open={state.opened}
                                onDropdownVisibleChange={(visible) => {
                                    setState((prev) => ({
                                        ...prev,
                                        opened: visible,
                                    }))
                                }}
                                className={`w-100 custom-select-react`}
                                mode={isSingleSelect() ? undefined : 'multiple'}
                                onSearch={(input) => {
                                    clearTimeout(searchTimeoutRef.current!)
                                    searchTimeoutRef.current = setTimeout(
                                        () => {
                                            fetchSearchData(input, (data) => {
                                                const transformedData =
                                                    data.map(
                                                        ({
                                                            value,
                                                            ...rest
                                                        }: any) => ({
                                                            id: value,
                                                            value,
                                                            ...rest,
                                                        })
                                                    )
                                                setState((prev) => ({
                                                    ...prev,
                                                    filteredOptions:
                                                        transformedData,
                                                }))
                                            })
                                        },
                                        150
                                    )
                                }}
                                filterOption={false}
                                placeholder={kedo.t('Search')}
                                value={vals}
                                onChange={(selected) => {
                                    if (!selected) {
                                        setState((prev) => ({
                                            ...prev,
                                            selected: [],
                                            filteredOptions: null,
                                        }))
                                        return
                                    }
                                    const selectedArray = Array.isArray(
                                        selected
                                    )
                                        ? selected
                                        : [selected]
                                    const newVal = selectedArray.map(
                                        ({ value, label }: any) => ({
                                            id: value,
                                            value,
                                            label,
                                        })
                                    )
                                    if (props.changeValue) {
                                        props.changeValue(
                                            props.item.id,
                                            newVal.map((item) => item.id)
                                        )
                                    }
                                    setState((prev) => ({
                                        ...prev,
                                        opened: false,
                                        selected: newVal,
                                        filteredOptions: null,
                                    }))
                                }}
                                suffixIcon={
                                    <FontAwesomeIcon icon={faChevronDown} />
                                }
                                notFoundContent={kedo.t('No items found')}
                            >
                                {(state.filteredOptions || filteredOptions).map(
                                    (option: any) => (
                                        <Option
                                            key={option.id}
                                            value={option.id}
                                        >
                                            {option.summary}
                                        </Option>
                                    )
                                )}
                            </SelectRC>
                            {isInvalid() ? (
                                <Typography variant="body2" color="error">
                                    {props.errors}
                                </Typography>
                            ) : null}
                        </Grid>
                    </Grid>
                </Box>
            )
        }

        if (state.selected?.length > 0) {
            state.selected.forEach((selected) => {
                checkSelectedId = state.options.find(
                    (item) => item.id === selected.id
                )
                if (checkSelectedId) {
                    matchCounter = matchCounter + 1
                }
            })
        }

        let totalOptions = state.options.length
        if (matchCounter > 0) {
            totalOptions = state.options.length - matchCounter
        }

        const isSingleSelectFlag = isSingleSelect()
        const linkFieldFooterSx = {
            mt: 'auto',
            backgroundColor: brandedTokens.variables.light,
            borderTop: '1px solid rgba(0,0,0,0.125)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            width: '100%',
            py: 1.5,
            minHeight: 65,
            '&:last-child': { pb: 1.5 },
        }

        if (
            props.item.settings &&
            props.item.settings.only_allow_add === true
        ) {
            return (
                <Box>
                    {state.addDossier ? renderAddDossier() : null}
                    {noHideAdd() &&
                    kedo
                        .env()
                        .isAllowedDefDossier(
                            'create',
                            getChildDefDossierId(),
                            kedo.user()
                        ) &&
                    (isSingleSelect() !== true ||
                        state.selected.length <= 0) ? (
                        <CompactButton
                            onClick={() =>
                                setState((prev) => ({
                                    ...prev,
                                    addDossier: true,
                                }))
                            }
                            variant={isInvalid() ? 'contained' : 'contained'}
                            color={isInvalid() ? 'error' : 'primary'}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </CompactButton>
                    ) : null}
                    <br />
                    <br />
                    <List>
                        {state.selected.map((item) => (
                            <ListItem key={item.id}>
                                {item.summary?.length > 0 ? (
                                    item.summary
                                ) : (
                                    <span>
                                        <Typography
                                            variant="body2"
                                            color="text.disabled"
                                            component="span"
                                            sx={{
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            ({kedo.t('No summary available')})
                                        </Typography>
                                    </span>
                                )}{' '}
                                {item.loading ? (
                                    <LoadingDefault as={'span'} />
                                ) : null}
                                <IconButton
                                    title={kedo.t('Delete')}
                                    size="small"
                                    color="error"
                                    onClick={() => removeSelectedItem(item)}
                                    sx={{ float: 'right' }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                    {isInvalid() ? (
                        <Typography variant="body2" color="error">
                            {props.errors}
                        </Typography>
                    ) : null}
                </Box>
            )
        }

        return (
            <Grid container className={'field-link'}>
                <Grid size={12}>
                    {state.showBrp ? (
                        <BrpModal
                            onClose={() =>
                                setState((prev) => ({
                                    ...prev,
                                    showBrp: false,
                                }))
                            }
                            enrichData={(personData) => {
                                const otherData = {
                                    environment_id: kedo
                                        .env()
                                        .getEnvironmentId(),
                                }
                                const data = Object.assign(
                                    {},
                                    personData,
                                    otherData
                                )
                                kedo.api()
                                    .post('/module/brp/person', data)
                                    .then((personResponse: any) => {
                                        addSelectedItem(personResponse.data)
                                        setState((prev) => ({
                                            ...prev,
                                            showBrp: false,
                                        }))
                                    })
                                    .catch((personErr: any) => {
                                        alert(
                                            'Er is iets mis gegaan met het koppelen van de BRP gegevens.'
                                        )
                                    })
                            }}
                            querystring={state.search}
                            kedo={kedo}
                        />
                    ) : null}
                    {state.addDossier ? renderAddDossier() : null}
                    {state.showFilter ? renderFilter() : null}
                    {isInvalid() ? (
                        <Typography variant="body2" color="error">
                            {props.errors}
                        </Typography>
                    ) : null}
                    <Grid container spacing={3} ref={innerRef}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card
                                sx={{
                                    borderRadius: 1,
                                    boxShadow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': {
                                        boxShadow: 4,
                                    },
                                    border: '1px solid rgba(0,0,0,0.125)',
                                    height: '100%',
                                }}
                            >
                                <CardHeader
                                    sx={{
                                        borderBottom:
                                            '1px solid rgba(0,0,0,0.125)',
                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                    }}
                                    title={
                                        <Typography variant="h6">
                                            {kedo.t('Options')}
                                        </Typography>
                                    }
                                    action={
                                        <Stack
                                            direction={'row'}
                                            sx={{
                                                float: 'right',
                                            }}
                                        >
                                            {state.linkSearch?.length > 0 ||
                                            Boolean(
                                                state.searchDossierQuery
                                            ) ? (
                                                <StyledButton
                                                    dense
                                                    title={kedo.t(
                                                        'Clear filter'
                                                    )}
                                                    onClick={() => {
                                                        setState((prev) => ({
                                                            ...prev,
                                                            linkSearch: null,
                                                            searchDossierQuery:
                                                                null,
                                                        }))
                                                        getOptions(
                                                            false,
                                                            undefined,
                                                            null,
                                                            null
                                                        )
                                                    }}
                                                    sx={{
                                                        '& svg': { mr: 0.5 },
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                    />
                                                    {kedo.t('Clear filter')}
                                                </StyledButton>
                                            ) : null}
                                            {noHideAdd() &&
                                            kedo
                                                .env()
                                                .isAllowedDefDossier(
                                                    'create',
                                                    getChildDefDossierId(),
                                                    kedo.user()
                                                ) &&
                                            (isSingleSelect() !== true ||
                                                state.selected.length <= 0) ? (
                                                <CompactButton
                                                    dense
                                                    onClick={() =>
                                                        setState((prev) => ({
                                                            ...prev,
                                                            addDossier: true,
                                                        }))
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPlus}
                                                    />
                                                </CompactButton>
                                            ) : null}
                                            <FilterToggleButton
                                                dense
                                                title={kedo.t('Filter')}
                                                aria-label={kedo.t('Filter')}
                                                variant="contained"
                                                active={
                                                    state.linkSearch?.length >
                                                        0 ||
                                                    Boolean(
                                                        state.searchDossierQuery
                                                    ) ||
                                                    state.showFilter
                                                }
                                                onClick={() => {
                                                    setState((prev) => ({
                                                        ...prev,
                                                        showFilter:
                                                            !prev.showFilter,
                                                        search: '',
                                                    }))
                                                    getOptions(
                                                        false,
                                                        undefined,
                                                        undefined,
                                                        undefined,
                                                        ''
                                                    )
                                                    fetchSearchFilterItems()
                                                }}
                                            />
                                        </Stack>
                                    }
                                />
                                <CardContent
                                    sx={{
                                        paddingBottom: 0,
                                        borderTop:
                                            '1px solid rgba(0,0,0,0.125)',
                                    }}
                                >
                                    <Box sx={{ marginBottom: '10px' }}>
                                        <TextField
                                            size="small"
                                            value={state.search}
                                            fullWidth
                                            type="text"
                                            onChange={(e) =>
                                                setState((prev) => ({
                                                    ...prev,
                                                    search: e.target.value,
                                                }))
                                            }
                                            onKeyUp={searchOption}
                                            placeholder={
                                                kedo.t('Search an option') +
                                                '...'
                                            }
                                            InputProps={{
                                                endAdornment:
                                                    state.search.length > 0 ? (
                                                        <IconButton
                                                            onClick={() => {
                                                                setState(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        search: '',
                                                                    })
                                                                )
                                                                getOptions(
                                                                    false,
                                                                    undefined,
                                                                    undefined,
                                                                    undefined,
                                                                    ''
                                                                )
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faTimes}
                                                            />
                                                        </IconButton>
                                                    ) : null,
                                            }}
                                        />
                                        {state.search.length > 0 &&
                                        props.item.settings?.show_brp &&
                                        (kedo.isAdminOrEnvironmentAdmin() ||
                                            kedo.env().hasCredential('brp')) &&
                                        state.search.match(
                                            /^(\d{7,9})|(\d{4}\s?[a-zA-Z]{2})\s(\d+)$/
                                        ) ? (
                                            <StyledButton
                                                fullWidth
                                                title={kedo.t('Search BRP')}
                                                onClick={() =>
                                                    setState((prev) => ({
                                                        ...prev,
                                                        showBrp: true,
                                                    }))
                                                }
                                                sx={{
                                                    mt: 1,
                                                    '& svg': { mr: 0.5 },
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPortrait}
                                                />
                                                {props.kedo.t('Search BRP')}
                                            </StyledButton>
                                        ) : null}
                                    </Box>
                                    <List
                                        sx={{
                                            backgroundColor: 'transparent',
                                        }}
                                        className={`link-options`}
                                    >
                                        {state.optionsLoading ? (
                                            <ListItem>
                                                <LoadingDefault />
                                            </ListItem>
                                        ) : null}
                                        {isSingleSelectFlag &&
                                        state.selected.length > 0 ? (
                                            <Alert severity="info">
                                                {props.kedo.t(
                                                    'Maximum number of items selected'
                                                )}
                                            </Alert>
                                        ) : null}
                                        {getSelectableOptions(
                                            state.options,
                                            state.selected
                                        ).map((item, index) => (
                                            <ListItemButton
                                                key={item.id}
                                                onClick={() =>
                                                    addSelectedItem(item)
                                                }
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    py: 1.5,
                                                    px: 2,
                                                    backgroundColor:
                                                        item.archived
                                                            ? 'action.disabledBackground'
                                                            : index % 2 === 0
                                                            ? 'rgba(243, 244, 247, .5)'
                                                            : 'transparent',
                                                    borderBottom:
                                                        '1px solid rgba(0,0,0,0.125)',
                                                    color: item.archived
                                                        ? 'text.disabled'
                                                        : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            item.archived
                                                                ? 'action.disabledBackground'
                                                                : '#e7e9ee',
                                                    },
                                                }}
                                            >
                                                {item.summary?.length > 0 &&
                                                props.item.settings?.show_id ? (
                                                    item.id
                                                ) : item.summary?.length > 0 ? (
                                                    item.summary
                                                ) : (
                                                    <span>
                                                        {item.id}{' '}
                                                        <Typography
                                                            variant="body2"
                                                            color="text.disabled"
                                                            component="span"
                                                            sx={{
                                                                fontSize:
                                                                    '0.75rem',
                                                            }}
                                                        >
                                                            (
                                                            {kedo.t(
                                                                'No summary available'
                                                            )}
                                                            )
                                                        </Typography>
                                                    </span>
                                                )}
                                            </ListItemButton>
                                        ))}
                                        {state.page < state.pages &&
                                        state.options.length !== state.total ? (
                                            <ListItemButton
                                                sx={{
                                                    backgroundColor:
                                                        brandedTokens.variables
                                                            .gray[100],
                                                }}
                                                key={
                                                    'load-more-' + props.item.id
                                                }
                                                onClick={() => getOptions(true)}
                                            >
                                                {state.optionsLoading ? (
                                                    <LoadingDefault />
                                                ) : null}{' '}
                                                {kedo.t('Load more')}...
                                            </ListItemButton>
                                        ) : null}
                                    </List>
                                </CardContent>
                                <CardContent sx={linkFieldFooterSx}>
                                    <Trans
                                        i18nKey="linkFieldSelected"
                                        totalOptions={totalOptions}
                                        totals={totals}
                                    >
                                        {{ totalOptions }} of a {{ totals }} of
                                        items found
                                    </Trans>
                                    {!isSingleSelectFlag ? (
                                        <StyledButton
                                            onClick={() => {
                                                const newItems =
                                                    getSelectableOptions(
                                                        state.options,
                                                        state.selected
                                                    )
                                                const select = state.selected
                                                newItems.forEach((newItem) => {
                                                    const existant =
                                                        select.find(
                                                            (opt) =>
                                                                opt.id ===
                                                                newItem.id
                                                        )
                                                    if (!existant) {
                                                        select.push(newItem)
                                                    }
                                                })
                                                if (props.changeValue) {
                                                    const ids = select.map(
                                                        (item) => item.id
                                                    )
                                                    props.changeValue(
                                                        props.item.id,
                                                        select
                                                    )
                                                }
                                                setState((prev) => ({
                                                    ...prev,
                                                    selected: select,
                                                }))
                                            }}
                                            size="small"
                                            sx={{
                                                fontSize: 'inherit',
                                                height: 'auto',
                                                py: 0.5,
                                                '& svg': { ml: 0.5 },
                                            }}
                                        >
                                            {kedo.t('select_all')}
                                            <FontAwesomeIcon
                                                icon={faAngleRight}
                                            />
                                        </StyledButton>
                                    ) : null}
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card
                                sx={{
                                    borderRadius: 1,
                                    boxShadow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': {
                                        boxShadow: 4,
                                    },
                                    border: '1px solid rgba(0,0,0,0.125)',
                                    height: '100%',
                                }}
                            >
                                <CardHeader
                                    sx={{
                                        borderBottom:
                                            '1px solid rgba(0,0,0,0.125)',
                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                    }}
                                    title={
                                        <Typography variant="h6">
                                            {kedo.t('Selected')}
                                        </Typography>
                                    }
                                />
                                <CardContent>
                                    <List
                                        sx={{
                                            backgroundColor: 'transparent',
                                        }}
                                        className={'link-selected'}
                                    >
                                        {state.selected.map((item, index) => (
                                            <ListItem
                                                key={item.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    backgroundColor:
                                                        item.archived
                                                            ? 'action.disabledBackground'
                                                            : index % 2 === 0
                                                            ? 'undefined'
                                                            : brandedTokens
                                                                  .variables
                                                                  .gray[100],
                                                    borderBottom:
                                                        '1px solid rgba(0,0,0,0.125)',
                                                }}
                                            >
                                                {item.summary?.length > 0 &&
                                                props.item.settings?.show_id ? (
                                                    item.id
                                                ) : item.summary?.length > 0 ? (
                                                    item.summary
                                                ) : (
                                                    <span>
                                                        {item.id}{' '}
                                                        <Typography
                                                            variant="body2"
                                                            color="text.disabled"
                                                            component="span"
                                                            sx={{
                                                                fontSize:
                                                                    '0.75rem',
                                                            }}
                                                        >
                                                            (
                                                            {kedo.t(
                                                                'No summary available'
                                                            )}
                                                            )
                                                        </Typography>
                                                    </span>
                                                )}
                                                {item.loading ? (
                                                    <LoadingDefault
                                                        as={'span'}
                                                    />
                                                ) : null}
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        removeSelectedItem(item)
                                                    }
                                                    sx={{
                                                        marginLeft: 'auto',
                                                        float: 'right',
                                                        padding: '.75rem',
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                    />
                                                </IconButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                                <CardContent sx={linkFieldFooterSx}>
                                    {state.selected.length}{' '}
                                    {isSingleSelectFlag ? ' of 1 ' : null}{' '}
                                    {props.kedo.t('Selected').toLowerCase()}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
    }

    const fetchSearchData = (
        inputValue: string,
        callBack: (data: any[]) => void
    ) => {
        const params = {
            params: {
                defDossier: getChildDefDossierId(),
                environment: props.kedo.env().getEnvironment().id,
                limit: 50,
            },
        }

        if (inputValue) {
            params.params.summary = inputValue
        }

        if (props.item.def_dossier_link.settings?.limit_output_to_mis_item) {
            params.params.dossierQuery =
                props.item.def_dossier_link.settings.limit_output_to_mis_item
        }

        if (props.embedded && props.item.settings?.limit_embedded_link) {
            params.params.linkId = props.item.settings.limit_embedded_link
            params.params.embedded = props.embedded
        }

        if (props.item?.settings?.sort?.length > 0) {
            params.params.sort = []
            params.params.sortOrder = []
            props.item.settings.sort.forEach((filterItem) => {
                params.params.sort.push(filterItem.dddfId)
                params.params.sortOrder.push(filterItem.sort)
            })
        }

        const idToCheck = state.value
        props.kedo
            .api()
            .get(props.kedo.api().getContentDossierEndpoint(), params)
            .then(async (response: any) => {
                const tempArray = []

                if (props.item.settings?.show_id) {
                    response.data.results.dossiers.forEach((element: any) => {
                        tempArray.push({
                            label: element.id,
                            value: element.id,
                            summary: element.summary,
                        })
                    })
                } else {
                    response.data.results.dossiers.forEach((element: any) => {
                        tempArray.push({
                            label: element.summary,
                            value: element.id,
                            summary: element.summary,
                        })
                    })
                }
                let idExists = false

                setState((prev) => ({ ...prev, optAsyn: tempArray }))
                if (callBack) callBack(tempArray)

                if (
                    Array.isArray(idToCheck) &&
                    idToCheck.length > 0 &&
                    idToCheck[0]?.id
                ) {
                    idExists = tempArray.some(
                        (item) => item.value === idToCheck[0].id
                    )
                }

                if (!idExists) {
                    const idToFetch =
                        idToCheck?.length > 0 ? idToCheck[0].id : null

                    if (idToFetch) {
                        props.kedo
                            .api()
                            .get(
                                `${props.kedo
                                    .api()
                                    .getContentDossierEndpoint()}/${idToFetch}`
                            )
                            .then(async (response: any) => {
                                const dossier = response.data.dossier
                                if (dossier) {
                                    const result = {
                                        id: dossier.id,
                                        label: dossier.summary,
                                        summary: dossier.summary,
                                    }
                                    setState((prev) => ({
                                        ...prev,
                                        value: result,
                                    }))
                                }
                            })
                            .catch((error: any) => {
                                console.error('Error dossier:', error)
                            })
                    }
                }
            })
    }

    const onSearchChange = (e: any) => {
        const ids = e?.map((item: any) => ({ id: item.value })) ?? []

        setState((prev) => ({
            ...prev,
            opened: false,
            value: e,
        }))

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            props.changeValue?.(props.item.id, ids)
        }, 500)
    }

    const normalize = (item: any) => {
        const value = item?.value ?? item?.id

        return {
            value,
            label: item?.label ?? item?.summary,
        }
    }

    const renderSearch = () => {
        if (!state.optAsyn) {
            return null
        }

        const selectedValues = Array.isArray(state.value) ? state.value : []

        const sourceOptions = state.filteredOptions || state.optAsyn

        const filteredOptions = sourceOptions.filter(
            (option) =>
                !selectedValues.some(
                    (selected) => selected.value === option.value
                )
        )

        return (
            <SelectRC
                labelInValue
                showSearch
                allowClear
                open={state.opened}
                onDropdownVisibleChange={(visible) => {
                    setState((prev) => ({ ...prev, opened: visible }))
                    if (visible) {
                        fetchSearchData('', (data: any[]) => {
                            const transformedData = data.map((item: any) => ({
                                value: item.value,
                                label: item.label,
                            }))
                            setState((prev) => ({
                                ...prev,
                                filteredOptions: transformedData,
                            }))
                        })
                    }
                }}
                ref={(ref) => {
                    innerRef.current = ref
                }}
                className="w-100 custom-select-react"
                mode="multiple"
                onChange={(selectedItems) => {
                    onSearchChange(selectedItems)
                    innerRef.current?.blur()
                }}
                onSearch={(inputValue: string) => {
                    clearTimeout(searchTimeoutRef.current!)
                    searchTimeoutRef.current = setTimeout(() => {
                        fetchSearchData(inputValue, (data: any[]) => {
                            const transformedData = data.map((item: any) => ({
                                value: item.value,
                                label: item.label,
                            }))
                            setState((prev) => ({
                                ...prev,
                                filteredOptions: transformedData,
                            }))
                        })
                    }, 150)
                }}
                filterOption={false}
                suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
                placeholder={`${props.kedo.t('Search')}...`}
                value={selectedValues.map(normalize)}
                autoClearSearchValue={true}
                notFoundContent={
                    state.search?.trim().length > 0 &&
                    filteredOptions.length === 0
                        ? props.kedo.t('No items found')
                        : null
                }
            >
                {filteredOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </SelectRC>
        )
    }

    const renderShowFancy = () => {
        return (
            <>
                <DefDossierList
                    addDossier={false}
                    showEmail={state.showEmail === true}
                    ddiSort={
                        props.item &&
                        props.item.settings &&
                        props.item.settings.sort
                            ? props.item.settings.sort
                            : null
                    }
                    defDossierId={
                        props.item.def_dossier_link.child_def_dossier_id
                    }
                    dossierQuery={state.dossierQuery}
                    embedded={props.dossier ? props.dossier.id : null}
                    filterDossier={null}
                    kedo={props.kedo}
                    linkId={props.item.def_dossier_link.back_link_id}
                    mode={'embeddedList'}
                    onClick={(dossier) => {
                        window.location.href = '/contentdossier/' + dossier.id
                    }}
                    ref={(ref) => (innerRef.current = ref)}
                    searchParams={null}
                />
            </>
        )
    }

    const renderShow = () => {
        if (!props.value || props.value.length <= 0) {
            return ''
        }

        if (props.item.settings?.show_embedded) {
            return renderShowFancy()
        }
        if (props.item.settings && props.item.settings.block) {
            return (
                <DisplayItemBlock
                    kedo={props.kedo}
                    misc={{ hideTitle: true }}
                    link={props.item.def_dossier_link.back_link_id}
                    settings={props.item.settings}
                    dossier={props.dossier?.id}
                    id={props.item.settings.block}
                />
            )
        }
        if (props.value.length === 1) {
            // One result
            if (props.item.settings?.clickable) {
                return (
                    <Link
                        onClick={() => {
                            setTimeout(
                                () =>
                                    (window.location.href = `/contentdossier/${
                                        props.value![0].id
                                    }`),
                                200
                            )
                        }}
                        to={`/contentdossier/${props.value[0].id}`}
                    >
                        <span key={props.value[0].id}>
                            {props.value[0] &&
                            props.value[0].summary &&
                            props.value[0].summary.length > 0 &&
                            props.item.settings.show_id ? (
                                props.value[0].id
                            ) : props.value[0].summary?.length > 0 ? (
                                props.value[0].summary
                            ) : (
                                <span>
                                    {props.value[0].id}{' '}
                                    <Typography
                                        variant="body2"
                                        color="text.disabled"
                                        component="span"
                                        sx={{
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        ({props.kedo.t('No summary available')})
                                    </Typography>
                                </span>
                            )}
                        </span>
                    </Link>
                )
            }
            return (
                <span className="contentData" key={props.value[0].id}>
                    {props.value[0] &&
                    props.value[0].summary?.length > 0 &&
                    props.item.settings?.show_id ? (
                        props.value[0].id
                    ) : props.value[0].summary?.length > 0 ? (
                        props.value[0].summary
                    ) : (
                        <span>
                            {props.value[0].id}{' '}
                            <Typography
                                variant="body2"
                                color="text.disabled"
                                component="span"
                                sx={{
                                    fontSize: '0.75rem',
                                }}
                            >
                                ({props.kedo.t('No summary available')})
                            </Typography>
                        </span>
                    )}
                </span>
            )
        }

        // Results are higher then 1
        if (props.item.settings && props.item.settings.clickable) {
            return (
                <ul>
                    {props.value &&
                        props.value.map &&
                        props.value.map((item) => (
                            <li key={item.id}>
                                <a href={'/contentdossier/' + item.id}>
                                    {item.summary?.length > 0 &&
                                    props.item.settings?.show_id ? (
                                        item.id
                                    ) : item.summary?.length > 0 ? (
                                        item.summary
                                    ) : (
                                        <span>
                                            {item.id}{' '}
                                            <Typography
                                                variant="body2"
                                                color="text.disabled"
                                                component="span"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                }}
                                            >
                                                (
                                                {props.kedo.t(
                                                    'No summary available'
                                                )}
                                                )
                                            </Typography>
                                        </span>
                                    )}
                                </a>
                            </li>
                        ))}
                </ul>
            )
        } else {
            return (
                <ul>
                    {props.value &&
                        props.value.map &&
                        props.value.map((item) => (
                            <li key={item.id}>
                                {item.summary?.length > 0 &&
                                props.item.settings?.show_id ? (
                                    item.id
                                ) : item.summary?.length > 0 ? (
                                    item.summary
                                ) : (
                                    <span>
                                        {item.id}{' '}
                                        <Typography
                                            variant="body2"
                                            color="text.disabled"
                                            component="span"
                                            sx={{
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            (
                                            {props.kedo.t(
                                                'No summary available'
                                            )}
                                            )
                                        </Typography>
                                    </span>
                                )}
                            </li>
                        ))}
                </ul>
            )
        }
    }

    const setDefaultValues = () => {
        getOptions(false, props.item.settings?.default_val_dos_query)
    }

    useEffect(() => {
        if (props.mode === 'edit' || props.mode === 'show/edit') {
            getOptions()
            fetchSearchData('', (values) => {
                setState((prev) => ({ ...prev, value: values }))
            })

            if (
                (!props.dossier || !props.dossier.id) &&
                props.item?.settings?.default_val_dos_query
            ) {
                setDefaultValues()
            }
        } else if (
            (props.mode === 'search' || props.mode === 'advanced-search') &&
            state.selected.length > 0
        ) {
            fetchSearchData('', (values) => {
                const newSelected = []

                state.selected.forEach((item) => {
                    const found = values.find((obj) => obj.value === item.id)

                    if (found) {
                        newSelected.push(found)
                    }
                })

                setState((prev) => ({ ...prev, value: newSelected }))
            })
        }

        if (props.item?.settings?.embedded_single_view === true) {
            fetchOtherDisplayPositions()
        }
    }, [props.mode, props.dossier, props.item])

    const renderEmbeddedView = () => {
        if (!isSingleSelect()) {
            return (
                <Alert severity="warning">
                    {props.kedo.t('A embedded view can only contain one link')}
                </Alert>
            )
        }

        if (!state.dpChildMain) {
            return null
        }

        if (state.selected.length > 1) {
            return (
                <Alert severity="warning">
                    {props.kedo.t(
                        'You cannot convert a multiselect to an embedded view'
                    )}
                </Alert>
            )
        } else if (state.selected.length === 1) {
            return (
                <ContentDossier
                    props={{
                        selectedTab: state.dpChildMain?.id,
                        existingUser: true,
                        modalized: true,
                        onClose: () => {
                            // TODO:
                        },
                        dossierId: state.selected[0].id,
                        disableBreadcrumb: true,
                    }}
                    kedo={props.kedo}
                />
            )
        }

        if (!props.item?.def_dossier_link?.child_def_dossier_id) {
            return <span>Loading</span>
        }

        return (
            <NewContentDossier
                props={{
                    kedo: props.kedo,
                    multiAdd: state.multiAdd,
                    defDossierId:
                        props.item.def_dossier_link.child_def_dossier_id,
                    embeddedDefDossierId: props.item.def_dossier_id,
                    embedded: props.dossier?.id,
                    embeddedFieldId: props.item.id,
                    linkId: props.item.def_dossier_link.back_link_id,
                    location: 'EmbeddedLinkField',
                    onSuccess: (dosId) => {
                        setState((prev) => ({ ...prev, loading: true }))
                        setTimeout(() => {
                            setState((prev) => ({
                                ...prev,
                                selected: [{ id: dosId }],
                                loading: false,
                            }))
                        }, 500)
                    },
                }}
                multiAdd={state.multiAdd}
                kedo={props.kedo}
                defDossierId={props.item.def_dossier_link.child_def_dossier_id}
                embeddedDefDossierId={props.item.def_dossier_id}
                leftSideBar={
                    props.item.settings && props.item.settings.passing_sidebar
                        ? props.item.settings.passing_sidebar
                        : null
                }
                embedded={props.dossier?.id}
                embeddedFieldId={props.item.id}
                linkId={props.item.def_dossier_link.back_link_id}
                location={'EmbeddedLinkField'}
                onClose={() =>
                    setState((prev) => ({ ...prev, addDossier: false }))
                }
                onSuccess={(dosId) => {
                    setState((prev) => ({ ...prev, loading: true }))
                    setTimeout(() => {
                        setState((prev) => ({
                            ...prev,
                            selected: [{ id: dosId }],
                            loading: false,
                        }))
                    }, 500)
                }}
                onContinueSuccess={(dosId) => {
                    // TODO
                }}
            />
        )
    }

    const fetchOtherDisplayPositions = () => {
        const dpFetchUrl =
            props.kedo.api().getDisplayPositionEndpoint() +
            '?defDossier=' +
            props.item.def_dossier_link.child_def_dossier_id +
            '&limit=100'

        props.kedo
            .api()
            .getCached(dpFetchUrl)
            .then((response: any) => {
                const dpFetchMain = response.data.results.find(
                    (item: any) => item.type === 'main_content'
                )
                setState((prev) => ({ ...prev, dpChildMain: dpFetchMain }))
            })
    }

    if (state.loading === true) {
        return <Loading kedo={props.kedo} />
    }

    if (props.item?.settings?.embedded_single_view === true) {
        return renderEmbeddedView()
    }

    if (props.mode === 'list') {
        return renderShow()
    } else if (props.mode === 'edit' || props.mode === 'show/edit') {
        return renderEdit()
    } else if (props.mode === 'search' || props.mode === 'advanced-search') {
        return renderSearch()
    }

    return renderShow()
})

LinkField.displayName = 'LinkField'

export default LinkField
